/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Button, IconButton, Tooltip } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import '../../styles/ManageProgramData.css';
import UserManagement from './UserManagement';
import DepartmentManagement from './DepartmentManagement';
import FacilityManagement from './FacilityManagement';
import CompartmentTypeManagement from './CompartmentTypeManagement';
import ChamberManagement from './ChamberManagement';
import SubstrateManagement from './SubstrateManagement';
import SpeciesManagement from './SpeciesManagement';
import LightingManagement from './LightingManagement';
import PotManagement from './PotManagement';
import UnitManagement from './UnitManagement';
import ResourceManagement from './ResourceManagement';
import WaterManagement from './WaterManagement';
import ProjectRoleManagement from './ProjectRoleManagement';
import InstitutionRoleManagement from './InstitutionRoleManagement';
import InvoiceTemplateManagement from './InvoiceTemplateManagement';
import EmailServiceManagement from './EmailServiceManagement';
import BrandingManagement from './BrandingManagement';

/*
 * Every section of the left pane. The order here is the built-in one, used
 * until an admin saves their own: it follows what a brand-new deployment has
 * to set up first — the kinds of space, the facility and its compartments, then what goes in
 * them, then the people, then the paperwork.
 */
const SECTIONS = [
  { id: 'compartment-types', label: 'Compartment Types', Component: CompartmentTypeManagement },
  { id: 'facilities', label: 'Facilities', Component: FacilityManagement },
  { id: 'units', label: 'Units', Component: UnitManagement },
  { id: 'chambers', label: 'Compartments', Component: ChamberManagement },
  { id: 'lighting', label: 'Lighting', Component: LightingManagement },
  { id: 'water', label: 'Water', Component: WaterManagement },
  { id: 'pots', label: 'Pots', Component: PotManagement },
  { id: 'substrates', label: 'Substrates', Component: SubstrateManagement },
  { id: 'species', label: 'Species', Component: SpeciesManagement },
  { id: 'resources', label: 'Resources', Component: ResourceManagement },
  { id: 'departments', label: 'Departments', Component: DepartmentManagement },
  { id: 'institution-roles', label: 'Institution Roles', Component: InstitutionRoleManagement },
  { id: 'project-roles', label: 'Project Roles', Component: ProjectRoleManagement },
  { id: 'users', label: 'Users', Component: UserManagement },
  { id: 'invoice-template', label: 'Invoice Template', Component: InvoiceTemplateManagement },
  { id: 'email-service', label: 'Email Service', Component: EmailServiceManagement },
  { id: 'branding', label: 'Branding', Component: BrandingManagement }
];

const DEFAULT_ORDER = SECTIONS.map(s => s.id);
const SECTION_BY_ID = Object.fromEntries(SECTIONS.map(s => [s.id, s]));

/* A saved order may predate a section being added or removed: keep the ids
   that still exist, then append any it has not heard of in built-in order. */
const normalizeOrder = (saved) => {
  const known = (saved || []).filter(id => SECTION_BY_ID[id]);
  return [...known, ...DEFAULT_ORDER.filter(id => !known.includes(id))];
};

const sameOrder = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);

const ManageProgramData = () => {
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [activeSection, setActiveSection] = useState(DEFAULT_ORDER[0]);

  // Reorder mode edits a draft; nothing changes for anyone until Save.
  const [reordering, setReordering] = useState(false);
  const [draft, setDraft] = useState(DEFAULT_ORDER);
  const [dragId, setDragId] = useState(null);
  const [saving, setSaving] = useState(false);
  const userPickedSection = useRef(false);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/program-data-settings')
      .then(({ data }) => {
        if (cancelled) return;
        const loaded = normalizeOrder(data.section_order);
        setOrder(loaded);
        // Open on whatever comes first, unless the admin already clicked
        // something while the order was loading.
        if (!userPickedSection.current) setActiveSection(loaded[0]);
      })
      .catch(err => console.error('Error loading Program Data order:', err));
    return () => { cancelled = true; };
  }, []);

  const selectSection = (id) => {
    userPickedSection.current = true;
    setActiveSection(id);
  };

  const startReordering = () => {
    setDraft(order);
    setReordering(true);
  };

  const moveDraft = (id, toIndex) => {
    setDraft(prev => {
      const from = prev.indexOf(id);
      if (from === -1 || toIndex < 0 || toIndex >= prev.length || from === toIndex) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(toIndex, 0, id);
      return next;
    });
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      // The built-in order is stored as empty, so a deployment that never
      // customised anything keeps following the default as it changes.
      const payload = sameOrder(draft, DEFAULT_ORDER) ? [] : draft;
      const { data } = await axios.put('/api/program-data-settings/section-order', { section_order: payload });
      setOrder(normalizeOrder(data.section_order));
      setReordering(false);
      toast.success('Program Data order saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the order');
    } finally {
      setSaving(false);
    }
  };

  const ActiveComponent = (SECTION_BY_ID[activeSection] || SECTION_BY_ID[order[0]]).Component;

  return (
    <div className="manage-program-container">
      <div className="sidebar">
        <div className="program-data-sidebar-heading">
          <h2>Program Data</h2>
          {!reordering && (
            <Tooltip title="Change the order of these tabs">
              <IconButton
                size="small"
                onClick={startReordering}
                aria-label="Change the order of the Program Data tabs"
              >
                <SwapVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {reordering ? (
          <>
            <p className="program-data-reorder-hint">
              Drag the tabs into the order admins should work through them, or use the arrows. Applies to every admin.
            </p>
            <ul className="program-data-reorder-list">
              {draft.map((id, index) => (
                <li
                  key={id}
                  draggable
                  className={dragId === id ? 'dragging' : ''}
                  onDragStart={(e) => {
                    setDragId(id);
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox will not start a drag without some data.
                    e.dataTransfer.setData('text/plain', id);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragId && dragId !== id) moveDraft(dragId, index);
                  }}
                  onDrop={(e) => e.preventDefault()}
                  onDragEnd={() => setDragId(null)}
                >
                  <DragIndicatorIcon fontSize="small" className="program-data-drag-handle" aria-hidden="true" />
                  <span className="program-data-reorder-label">{SECTION_BY_ID[id].label}</span>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => moveDraft(id, index - 1)}
                    aria-label={`Move ${SECTION_BY_ID[id].label} up`}
                  >
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === draft.length - 1}
                    onClick={() => moveDraft(id, index + 1)}
                    aria-label={`Move ${SECTION_BY_ID[id].label} down`}
                  >
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </li>
              ))}
            </ul>
            <div className="program-data-reorder-actions">
              <Button variant="contained" size="small" onClick={saveOrder} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button size="small" onClick={() => setReordering(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                size="small"
                onClick={() => setDraft(DEFAULT_ORDER)}
                disabled={saving || sameOrder(draft, DEFAULT_ORDER)}
              >
                Reset to default
              </Button>
            </div>
          </>
        ) : (
          <ul>
            {order.map(id => (
              <li key={id}>
                <button
                  type="button"
                  className={activeSection === id ? 'active' : ''}
                  aria-current={activeSection === id ? 'page' : undefined}
                  onClick={() => selectSection(id)}
                >{SECTION_BY_ID[id].label}</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="content-area">
        <ActiveComponent />
      </div>
    </div>
  );
};

export default ManageProgramData;
